import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogLevel, OpenIdConfiguration, StsConfigLoader } from 'angular-auth-oidc-client';
import { firstValueFrom, Observable, defer, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap, timeout } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
    logPublicConfigurationFallback,
    logPublicConfigurationRequest,
} from './configuration-diagnostics';

const PUBLIC_CONFIGURATION_PATH = 'settings/public';
const SUPPORTED_SCHEMA_VERSION = 1;
const SCOPE_PATTERN = /^[A-Za-z0-9._:-]+$/;
const REFRESH_TOKEN_SCOPE = 'offline_access';

export interface PublicAppConfiguration {
    schemaVersion: number;
    environment: string;
    revision: number;
    authentication: {
        authority: string;
        clientId: string;
        scopes: string[];
    };
    appVersion: {
        web: string;
        webApi: string;
        fastApi: string;
    };
}

export type PlatformConfigurationSource = 'central' | 'local';

class PublicConfigurationValidationError extends Error {
    constructor(readonly code: string) {
        super(code);
    }
}

/**
 * Resolves the OIDC configuration before authentication starts.
 *
 * The public endpoint is called through HttpBackend so the request bypasses
 * application interceptors. This prevents a startup cycle where the bearer
 * interceptor needs AuthService before the OIDC client has been configured,
 * and guarantees that no token or tenant header is sent on this anonymous call.
 */
@Injectable({ providedIn: 'root' })
export class PlatformStsConfigurationLoader extends StsConfigLoader {
    private readonly configurations$: Observable<OpenIdConfiguration[]>;
    private publicConfigurationValue: PublicAppConfiguration | null = null;
    private configurationSourceValue: PlatformConfigurationSource = 'local';
    private effectiveConfigurationValue: OpenIdConfiguration | null = null;

    constructor(httpBackend: HttpBackend) {
        super();

        const localConfiguration = this.createOidcConfiguration(
            environment.stsAuthority,
            environment.clientId,
            environment.scope
        );
        const startupSettings = environment.platformConfiguration;
        const rawHttpClient = new HttpClient(httpBackend);

        const selectedConfiguration$ = startupSettings.enabled
            ? defer(() => {
                  const endpoint = this.buildPublicConfigurationUrl();
                  logPublicConfigurationRequest(endpoint);
                  return rawHttpClient.get<unknown>(endpoint);
              }).pipe(
                  timeout(startupSettings.requestTimeoutMs),
                  map((payload) => this.parsePublicConfiguration(payload)),
                  tap((configuration) => {
                      this.publicConfigurationValue = configuration;
                      this.configurationSourceValue = 'central';
                  }),
                  map((configuration) =>
                      this.createOidcConfiguration(
                          configuration.authentication.authority,
                          configuration.authentication.clientId,
                          configuration.authentication.scopes.join(' ')
                      )
                  ),
                  tap((configuration) => {
                      this.effectiveConfigurationValue = configuration;
                  }),
                  catchError((error: unknown) => {
                      const failureCode = this.classifyFailure(error);
                      logPublicConfigurationFallback(
                          this.publicConfigurationEndpointForDiagnostics(),
                          failureCode
                      );

                      if (startupSettings.required) {
                          return throwError(
                              () =>
                                  new Error(
                                      `Required public platform configuration could not be loaded (${failureCode}).`
                                  )
                          );
                      }

                      return of(localConfiguration).pipe(
                          tap((configuration) => {
                              this.effectiveConfigurationValue = configuration;
                          })
                      );
                  })
              )
            : of(localConfiguration).pipe(
                  tap((configuration) => {
                      this.effectiveConfigurationValue = configuration;
                  })
              );

        this.configurations$ = selectedConfiguration$.pipe(
            map((configuration) => [configuration]),
            shareReplay({ bufferSize: 1, refCount: false })
        );
    }

    loadConfigs(): Observable<OpenIdConfiguration[]> {
        return this.configurations$;
    }

    /** Ensures required configuration failures stop Angular bootstrap. */
    async initialize(): Promise<void> {
        await firstValueFrom(this.configurations$);
    }

    get source(): PlatformConfigurationSource {
        return this.configurationSourceValue;
    }

    get publicConfiguration(): PublicAppConfiguration | null {
        return this.publicConfigurationValue;
    }

    get effectiveConfiguration(): OpenIdConfiguration | null {
        return this.effectiveConfigurationValue;
    }

    private createOidcConfiguration(
        authority: string,
        clientId: string,
        scope: string
    ): OpenIdConfiguration {
        return {
            // The OIDC client appends `/.well-known/openid-configuration`.
            authority: authority.trim().replace(/\/+$/, ''),
            clientId: clientId.trim(),
            redirectUrl: `${environment.clientRoot}signin-callback`,
            postLogoutRedirectUri: `${environment.clientRoot}signout-callback`,
            responseType: 'code',
            // Refresh-token renewal requires the standard OIDC offline_access
            // scope. Apply this to both central and compiled fallback config so
            // the effective runtime configuration is consistent.
            scope: this.ensureRefreshTokenScope(scope),
            maxIdTokenIatOffsetAllowedInSeconds: 300,
            silentRenew: true,
            useRefreshToken: true,
            ignoreNonceAfterRefresh: true,
            triggerRefreshWhenIdTokenExpired: false,
            renewTimeBeforeTokenExpiresInSeconds: 60,
            autoUserInfo: true,
            triggerAuthorizationResultEvent: true,
            logLevel: LogLevel.Warn,
        };
    }

    private ensureRefreshTokenScope(scope: string): string {
        const scopes = scope.trim().split(/\s+/).filter(Boolean);

        if (!scopes.includes(REFRESH_TOKEN_SCOPE)) {
            scopes.push(REFRESH_TOKEN_SCOPE);
        }

        return scopes.join(' ');
    }

    private buildPublicConfigurationUrl(): string {
        const apiRoot = environment.apiRoot.trim();
        const separator = apiRoot.endsWith('/') ? '' : '/';
        const endpoint = new URL(`${apiRoot}${separator}${PUBLIC_CONFIGURATION_PATH}`);

        if (
            endpoint.username ||
            endpoint.password ||
            endpoint.search ||
            endpoint.hash ||
            !this.isAllowedTransport(endpoint)
        ) {
            throw new PublicConfigurationValidationError('invalid-endpoint');
        }

        return endpoint.toString();
    }

    private publicConfigurationEndpointForDiagnostics(): string {
        try {
            return this.buildPublicConfigurationUrl();
        } catch {
            return '[invalid local public-configuration endpoint]';
        }
    }

    private parsePublicConfiguration(payload: unknown): PublicAppConfiguration {
        const root = this.requireRecord(payload, 'invalid-payload');
        const authentication = this.requireRecord(root['authentication'], 'invalid-authentication');
        const appVersion = this.requireRecord(root['appVersion'], 'invalid-app-version');

        const schemaVersion = root['schemaVersion'];
        if (schemaVersion !== SUPPORTED_SCHEMA_VERSION) {
            throw new PublicConfigurationValidationError('unsupported-schema');
        }

        const revision = root['revision'];
        if (!Number.isSafeInteger(revision) || (revision as number) < 1) {
            throw new PublicConfigurationValidationError('invalid-revision');
        }

        const environmentName = this.requireString(root['environment'], 'invalid-environment', 128);
        const authority = this.requireAuthority(authentication['authority']);
        const clientId = this.requireString(authentication['clientId'], 'invalid-client-id', 256);
        const scopes = this.requireScopes(authentication['scopes']);

        return {
            schemaVersion,
            environment: environmentName,
            revision: revision as number,
            authentication: {
                authority,
                clientId,
                scopes,
            },
            appVersion: {
                web: this.requireVersion(appVersion['web']),
                webApi: this.requireVersion(appVersion['webApi']),
                fastApi: this.requireVersion(appVersion['fastApi']),
            },
        };
    }

    private requireAuthority(value: unknown): string {
        const authority = this.requireString(value, 'invalid-authority', 2048);

        let parsed: URL;
        try {
            parsed = new URL(authority);
        } catch {
            throw new PublicConfigurationValidationError('invalid-authority');
        }

        if (
            parsed.username ||
            parsed.password ||
            parsed.search ||
            parsed.hash ||
            !this.isAllowedTransport(parsed)
        ) {
            throw new PublicConfigurationValidationError('invalid-authority');
        }

        return authority.replace(/\/+$/, '');
    }

    private requireScopes(value: unknown): string[] {
        if (!Array.isArray(value) || value.length === 0 || value.length > 50) {
            throw new PublicConfigurationValidationError('invalid-scopes');
        }

        const scopes = value.map((scope) => {
            const normalized = this.requireString(scope, 'invalid-scopes', 128);
            if (!SCOPE_PATTERN.test(normalized)) {
                throw new PublicConfigurationValidationError('invalid-scopes');
            }
            return normalized;
        });
        const distinctScopes = [...new Set(scopes)];

        if (!distinctScopes.includes('openid')) {
            throw new PublicConfigurationValidationError('missing-openid-scope');
        }

        return distinctScopes;
    }

    private requireVersion(value: unknown): string {
        if (typeof value !== 'string' || value.length > 64) {
            throw new PublicConfigurationValidationError('invalid-app-version');
        }

        return value.trim();
    }

    private requireRecord(value: unknown, code: string): Record<string, unknown> {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            throw new PublicConfigurationValidationError(code);
        }

        return value as Record<string, unknown>;
    }

    private requireString(value: unknown, code: string, maximumLength: number): string {
        if (typeof value !== 'string') {
            throw new PublicConfigurationValidationError(code);
        }

        const normalized = value.trim();
        if (!normalized || normalized.length > maximumLength) {
            throw new PublicConfigurationValidationError(code);
        }

        return normalized;
    }

    private isAllowedTransport(url: URL): boolean {
        return (
            url.protocol === 'https:' ||
            (url.protocol === 'http:' && this.isLoopbackHost(url.hostname))
        );
    }

    private isLoopbackHost(hostname: string): boolean {
        const normalized = hostname.toLowerCase();
        return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '[::1]';
    }

    private classifyFailure(error: unknown): string {
        if (error instanceof PublicConfigurationValidationError) {
            return error.code;
        }

        if (error instanceof HttpErrorResponse) {
            return error.status > 0 ? `http-${error.status}` : 'network-error';
        }

        if (error instanceof Error && error.name === 'TimeoutError') {
            return 'timeout';
        }

        return 'unexpected-error';
    }
}
